// Engine authentication panel (Settings). Shows whether the Claude sign-in
// the engine runs on is healthy, and - for the owner - two ways to restore
// it when it is not:
//   1. the guided flow: the server drives the official `claude setup-token`
//      CLI; this panel opens its sign-in link and relays the pasted
//      authorization code back
//   2. paste a token produced by running `claude setup-token` on any
//      machine (tokens are account-scoped, not machine-scoped)

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useEngineAuth } from "../api";

type FlowStep = "idle" | "starting" | "code" | "finishing";

async function postJson<T>(url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `${res.status} ${res.statusText}`);
  return data;
}

export function EngineAuthPanel() {
  const { data: info, isLoading } = useEngineAuth();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<FlowStep>("idle");
  const [url, setUrl] = useState<string | null>(null);
  const [rawOutput, setRawOutput] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [pasteToken, setPasteToken] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (isLoading) return null;
  if (info === null || info === undefined) return null; // engine disabled

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: ["engine-auth"] });

  const stateChip =
    info.state === "ok" ? (
      <span className="chip good">signed in</span>
    ) : info.state === "warn" ? (
      <span className="chip warn">renew soon</span>
    ) : (
      <span className="chip critical">expired</span>
    );

  const begin = async () => {
    setError(null);
    setMessage(null);
    setRawOutput(null);
    setUrl(null);
    setStep("starting");
    try {
      const r = await postJson<{ ok: boolean; url?: string; output: string; error?: string }>(
        "/api/engine/auth/start",
      );
      if (!r.ok) throw new Error(r.error ?? "could not start the sign-in helper");
      setUrl(r.url ?? null);
      setRawOutput(r.url ? null : r.output);
      setStep("code");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStep("idle");
    }
  };

  const submitCode = async () => {
    setError(null);
    setStep("finishing");
    try {
      const r = await postJson<{ ok: boolean; output: string; error?: string }>(
        "/api/engine/auth/code",
        { code },
      );
      if (!r.ok) {
        setRawOutput(r.output);
        throw new Error(r.error ?? "sign-in did not complete");
      }
      setMessage("Signed in. The engine now uses a long-lived token.");
      setStep("idle");
      setCode("");
      setUrl(null);
      setRawOutput(null);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStep("code");
    }
  };

  const cancel = async () => {
    try {
      await postJson("/api/engine/auth/cancel");
    } catch {
      // Nothing to cancel is fine.
    }
    setStep("idle");
    setUrl(null);
    setRawOutput(null);
    setCode("");
  };

  const saveToken = async () => {
    setError(null);
    setMessage(null);
    try {
      await postJson("/api/engine/auth/token", { token: pasteToken });
      setMessage("Token saved. The engine now uses it for every run.");
      setPasteToken("");
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const removeToken = async () => {
    setError(null);
    setMessage(null);
    try {
      const res = await fetch("/api/engine/auth/token", { method: "DELETE" });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      setMessage("Token removed. The engine falls back to the server's own sign-in.");
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="panel">
      <h2>Engine authentication</h2>
      <div className="sub" style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {stateChip}
        <span>{info.detail}</span>
      </div>
      {info.refreshExpiresAt && info.state !== "expired" && (
        <div className="sub">
          Sign-in valid until {new Date(info.refreshExpiresAt).toLocaleString()}.
        </div>
      )}

      {!info.manage && info.state !== "ok" && (
        <div className="sub">
          Ask the owner to re-authenticate the engine before launching runs.
        </div>
      )}

      {message && <div className="sub">{message}</div>}
      {error && <div className="error-box">{error}</div>}

      {info.manage && (
        <>
          {step === "idle" && (
            <div className="sub" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button className="btn" onClick={() => void begin()}>
                {info.state === "ok" ? "Re-authenticate" : "Fix sign-in"}
              </button>
              {info.tokenSet && (
                <button className="btn secondary" onClick={() => void removeToken()}>
                  Remove stored token
                </button>
              )}
            </div>
          )}

          {step === "starting" && <div className="sub">Starting the sign-in helper on the server...</div>}

          {(step === "code" || step === "finishing") && (
            <div className="sub" style={{ display: "grid", gap: 8 }}>
              {url ? (
                <div>
                  <strong>Step 1.</strong>{" "}
                  <a href={url} target="_blank" rel="noreferrer">
                    Open the Claude sign-in page
                  </a>{" "}
                  and sign in with the account whose subscription runs the engine.
                </div>
              ) : (
                <div>
                  <strong>Step 1.</strong> The helper did not print a clean link;
                  follow its output below (open the URL it shows in a new tab).
                </div>
              )}
              <div>
                <strong>Step 2.</strong> After signing in, copy the authorization
                code the page shows and paste it here:
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input
                  type="text"
                  value={code}
                  placeholder="paste the authorization code"
                  onChange={(e) => setCode(e.target.value)}
                  style={{ minWidth: 280 }}
                  disabled={step === "finishing"}
                />
                <button
                  className="btn"
                  disabled={step === "finishing" || code.trim().length === 0}
                  onClick={() => void submitCode()}
                >
                  {step === "finishing" ? "Finishing..." : "Finish sign-in"}
                </button>
                <button
                  className="btn secondary"
                  disabled={step === "finishing"}
                  onClick={() => void cancel()}
                >
                  Cancel
                </button>
              </div>
              {rawOutput && (
                <pre
                  style={{ maxHeight: 200, overflow: "auto", whiteSpace: "pre-wrap" }}
                >
                  {rawOutput}
                </pre>
              )}
            </div>
          )}

          {step === "idle" && (
            <details className="sub">
              <summary>Or paste a token from another machine</summary>
              <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
                <div>
                  Run <code>claude setup-token</code> in a terminal on any machine
                  where Claude Code is installed, complete the sign-in it opens,
                  and paste the printed token (starts with <code>sk-ant-oat</code>):
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <input
                    type="password"
                    value={pasteToken}
                    placeholder="sk-ant-oat01-..."
                    onChange={(e) => setPasteToken(e.target.value)}
                    style={{ minWidth: 280 }}
                  />
                  <button
                    className="btn"
                    disabled={pasteToken.trim().length === 0}
                    onClick={() => void saveToken()}
                  >
                    Save token
                  </button>
                </div>
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
}
